import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import type { AccountLastNote } from '@/lib/types';
import {
  ACCOUNT_NOTE_FORMER_COLLEAGUE,
  ACCOUNT_NOTE_NOT_VISIBLE,
  ACCOUNT_NO_NOTES,
  accountLastNoteAuthor,
} from './account-last-note';

/*
 * The two account-wide "last note" cells. Wording and the three-state rule live
 * in `./account-last-note` — read its header before changing either cell.
 *
 * `undefined` (an older server) renders a bare dash with no claim; `null` (no
 * human note on the account) renders "No notes". They are different facts.
 */

/*
 * Re-exported so a caller written against the contract (constants and helpers
 * imported from this file) resolves. The canonical home is
 * `./account-last-note`; prefer importing from there.
 */
// oxlint-disable-next-line react/only-export-components
export { ACCOUNT_LAST_NOTE_AT_LABEL, ACCOUNT_LAST_NOTE_LABEL } from './account-last-note';
// oxlint-disable-next-line react/only-export-components
export { accountLastNoteDate, accountLastNoteFilterText } from './account-last-note';

const DASH = '—';

interface CellProps {
  note: AccountLastNote | null | undefined;
}

/** The NOTE column: author, then the body clamped; full body on `title`. */
export function AccountLastNoteCell({ note }: CellProps) {
  if (note === undefined) return <span className="text-fg-subtle">{DASH}</span>;
  if (note === null) return <span className="text-fg-subtle">{ACCOUNT_NO_NOTES}</span>;
  if (!note.bodyVisible) {
    return <span className="text-fg-muted italic">{ACCOUNT_NOTE_NOT_VISIBLE}</span>;
  }

  const author = accountLastNoteAuthor(note);
  const body = note.body ?? '';
  const formerColleague = author === ACCOUNT_NOTE_FORMER_COLLEAGUE;

  /*
   * The author leads the SAME paragraph as the body (owner's decision), so a
   * long note wraps under the name and the clamp counts both together.
   * `whitespace-pre-line` because a body carries newlines; the clamp is a
   * ceiling on the row height (a body may be 20,000 characters), and the whole
   * text is on the `title`.
   *
   * A real person's name is set in the brand accent AND bold, so colour is never
   * the only signal. "Former colleague" keeps its muted italic: it is not a name,
   * and dressing it as one would conflate the two.
   */
  return (
    <div
      className="min-w-0 whitespace-pre-line [overflow-wrap:anywhere] line-clamp-4 text-fg-secondary"
      title={author ? `${author}: ${body}` : body}
    >
      {author && (
        <>
          <span
            className={cx(
              formerColleague ? 'font-medium text-fg-muted italic' : 'font-semibold text-accent-brand',
            )}
          >
            {author}
          </span>
          <span className="text-fg-subtle">
            {' · '}
          </span>
        </>
      )}
      {body}
    </div>
  );
}

/**
 * The DATE column. Shown to every reader — the date is not private.
 *
 * ⚠️ Rendered in the reader's LOCAL time, while the `lastNoteAfter` /
 * `lastNoteBefore` filter compares UTC days — a note written just after
 * midnight UTC can read as the previous day here. The exact instant is on the
 * `title`.
 */
export function AccountLastNoteDateCell({ note }: CellProps) {
  if (note === undefined) return <span className="text-fg-subtle">{DASH}</span>;
  if (note === null) return <span className="text-fg-subtle">{ACCOUNT_NO_NOTES}</span>;
  return (
    <span className="tabular-nums text-fg-secondary" title={note.createdAt}>
      {formatDateTime(note.createdAt)}
    </span>
  );
}
